import {
  applyAcrossSlices,
  applyCommentMark,
  applyStyleMark,
  censusOfBlocks,
  censusOfHtml,
  changeFragmentCase,
  clearFragmentMarks,
  createDoczHandle,
  type DoczBlockKind,
  type DoczFragment,
  type DoczHandle,
  type DoczModule,
  type FragmentRun,
  fragmentText,
  marksAcrossSlices,
  MIXED,
  normalizeFragmentRuns,
  parseBlockAddress,
  renderFragmentToHtml,
  resolveStyle,
  sameMarks,
  setFragmentKind,
  type StructuredBlock,
  structuredFragmentAt,
  type StructuralLossItem,
  structuralLoss,
  type StyleMark,
  STYLE_PROPS,
  type StyleSlice,
  textFragment,
  toggleStyleMark,
} from "@norkron/web-docz";
import {
  type ActiveFormat,
  type ChangeCaseMode,
  createFragmentFormatApi,
  type FormatApi,
  type FragmentEditorHandle,
  type ParaPropsPatch,
  type TextEdit,
  type ToggleMarkType,
  type ToggleResult,
} from "@norkron/web-elaborate-editor";
import type { DoczBlock } from "@norkron/web-docz";
import {
  type CanvasEditorHandle,
  canvasDoczBlocks,
} from "@norkron/web-page-layout";
import {
  FormatBridge,
  foldFragmentIntoHandle,
  overridesOf,
} from "./canvasFormatBridge";

/**
 * The C2 FORMAT LAYER for the Canvas editor — the mark model the transport
 * `DoczBlock` (plain-text + comments only) cannot hold.
 *
 * Like the DOM `web-elaborate-editor` controller, we keep a per-block
 * `Map<blockId, DoczFragment>` of STYLED runs (`overrides`). A toolbar verb reads
 * the focused block's fragment (its override, or a plain seed built from the live
 * `DoczHandle` block + its comment spans), runs the REAL docz transform verb
 * (`toggleStyleMark` / `applyStyleMark` / `clearFragmentMarks` /
 * `changeFragmentCase` / `setFragmentKind` — all in `docz.wasm`), stores the
 * result back, and asks the Canvas engine to RE-LAYOUT through the mark-aware
 * `makeDoczEncodeBlocks(fragmentFor)` so the painted glyphs pick up the marks the
 * `pl_layout` pass can carry (bold weight + colour). `activeFormat()` reads the
 * marks at the caret so the ribbon lights its buttons.
 *
 * Reuse (RULE ZERO / HR13): the whole `FormatApi` is `createFragmentFormatApi`
 * over a {@link FragmentEditorHandle} that targets the FOCUSED block — the SAME
 * proven format command layer the DOM `<CommentEditor>` uses; we never re-derive
 * the toggle/active logic.
 *
 * PAINT STATUS (pdfz canvas-parity v2 — all five now render on the Canvas):
 * `bold` → the bold face, `color` → the run colour, `italic` → a synthesised-oblique
 * face (`face.italic===1`, the renderer shears the glyphs), `underline`/`strikeout`
 * → the SoA v2 `deco` run bits (the renderer strokes the lines). Inline marks are
 * SINGLE-BLOCK; a selection spanning blocks formats nothing (multi-block is a
 * follow-up). Typing INTO a formatted block resets that block's inline marks for
 * the optimistic frame (the Canvas input path owns text; the mark re-base is a
 * follow-up) — `reconcile()` drops a stale override the moment its text diverges,
 * so paint is never wrong.
 *
 * ── COORDINATE SPACES (P3-6). The layer works in TWO byte spaces and must never
 * confuse them:
 *
 *   · CANVAS space — the VISIBLE text of the structured block the engine paints.
 *     The caret's `byteOffset`, every selection range, every mark range and every
 *     comment anchor live here. `canvasSeed` / `canvasText` / `getFragment`.
 *   · LEGACY space — `handle.blocks[ord].text`. For a bare heading/paragraph it is
 *     the same string; for the opaque `kind:'html'` transport block it is VERBATIM
 *     MARKUP (`html_ingest.TransportKind` lowers every non-bare block to it). Only
 *     serialization lives here. `legacySeedOf` / `untouchedFragment`.
 *
 * Conflating them is what made `activeFormat()` blind over demonstrably bold text
 * and made every format verb address `"<p><str"` instead of the reviewer's
 * selection. See `canvasActiveFormat.test.ts` for the measured reproduction.
 *
 * ── AND THE TWO SPACES HAVE DIFFERENT IDENTITIES, NOT JUST DIFFERENT BYTES.
 * The canvas view flattens a structured container into one block PER EDITABLE
 * REGION, each named by a sub-block ADDRESS (`b1::li:0`, `b1::bq:0` — web-docz
 * `block-address.ts`), while the legacy transport holds the whole island under
 * ONE id. So an override recorded at the caret is keyed by an address the save
 * path's `overrides.get(block.id)` could never match: measured live, ribbon Bold
 * on a list item moved the raster by +478 ink pixels and `toHtml()` came back
 * byte-identical — the mark was typed, seen, saved and lost.
 *
 * The save therefore resolves overrides through the ADDRESS, not through the
 * legacy key: {@link overridesRootedAt} + {@link overriddenIslandHtml}. See
 * `canvasSubBlockSave.test.ts`, and `src/tests/regression_mark_fidelity.zig`
 * for the server half of the reload proof.
 */

export interface FormatLayerDeps {
  /** The live Canvas editor handle (its `.core` owns the selection + relayout). */
  readonly getEditor: () => CanvasEditorHandle | null;
  /** The latest `DoczHandle` after the newest Canvas edit (block text/comments). */
  readonly getHandle: () => DoczHandle;
  /** The per-block styled-run override map (the paint + save source for marks). */
  readonly overrides: Map<string, DoczFragment>;
  /** The Canvas root element (for the fragment handle's `root`). */
  readonly getRoot: () => HTMLElement | null;
}

export interface CanvasFormatLayer {
  readonly formatApi: FormatApi;
  toggleMark(type: ToggleMarkType): ToggleResult;
  setMark(mark: StyleMark): DoczFragment | null;
  clearFormatting(): DoczFragment | null;
  changeCase(mode: ChangeCaseMode): DoczFragment | null;
  setBlockKind(kind: DoczBlockKind, level?: number): DoczFragment | null;
  setParaProps(patch: ParaPropsPatch): DoczFragment | null;
  /**
   * C3 — place a comment Mark over the live canvas selection (or an explicit
   * byte range on the focused block) via the REAL docz `applyCommentMark` verb,
   * store each marked fragment as its block's override (so paint + `toHtml` carry
   * it), and re-layout. NEVER a hand-rolled TS mark — the run-split/overlap-union
   * lives in `docz.wasm`.
   *
   * ⚠ A COMMENT SPANS BLOCKS. It used to resolve a cross-block selection to a
   * COLLAPSED range on the focus block and decline, so the owner-reported defect
   * ("I can't select multiple paragraphs and add a comment") was a silent `null`.
   * A comment-as-Mark over N blocks IS N blocks each carrying
   * `Mark{.comment, commentId}` over its own sub-range — the docz-native
   * representation, needing no new IR concept (brief §6).
   *
   * `null` ONLY when there is no ranged selection at all. Otherwise a
   * {@link PlacedComment} whose `spans` are the blocks that took the Mark and
   * whose `declined` NAMES the ones that could not (an all-empty table cell has
   * no text to mark; a table / code island names no writable run slice). A
   * partial placement is REPORTED, never silently dropped and never a reason to
   * abort the whole comment — the durable anchor lives in `el.comment_anchors`,
   * not in the IR.
   */
  placeComment(commentId: string, range?: { start: number; end: number }): PlacedComment | null;
  activeFormat(): ActiveFormat;
  /** The block's STYLED fragment (its live override, else a plain seed carrying the
   *  block's comment marks). The mark-bearing read the clipboard copy path needs —
   *  `handle.blocks[ord]` is plain text and would silently drop every mark. */
  fragmentFor(ord: number): DoczFragment;
  /** Apply an out-of-band char edit to the block with `blockId` (programmatic). */
  applyTextEdit(blockId: string, edit: TextEdit): boolean;
  /** The whole document's export HTML WITH inline marks (the Save witness). */
  toHtml(): string;
  /**
   * THE SAVE REFUSAL PREDICATE — every structure class {@link toHtml} would
   * DESTROY relative to the document currently in memory. Empty means the write
   * is safe.
   *
   * It compares the LIVE model (`handle.toBlocks()` — tables, lists, quotes, code,
   * rules, images) against the string about to be posted, parsed back with the
   * native DOM. It is not a diff: a table the reviewer deleted is already gone from
   * the model, so an intended deletion never trips it. Only a SERIALIZER that
   * destroys structure the model still holds does — which is precisely the bug
   * class that shipped, and the one no test caught because every test asserted the
   * model rather than the bytes.
   *
   * A runtime with no DOM cannot parse and therefore cannot honestly measure; it
   * reports NO loss rather than a fabricated one, and the server-side guard remains
   * the authority (see the module doc).
   */
  structuralLossOnSave(): readonly StructuralLossItem[];
  /** Drop any override whose block text has diverged (typing) — call each publish. */
  reconcile(): void;
}

const EMPTY_ACTIVE_FORMAT: ActiveFormat = {
  marks: new Set(),
  link: null,
  color: null,
  highlight: null,
  fontFamily: null,
  fontSize: null,
  underlineStyle: null,
  letterSpacing: null,
  // FEATURE BUNDLE 3 (W2/W3) — no char-decoration active in the empty format.
  textEffects: null,
  charBorder: null,
  emphasis: null,
  kind: "paragraph",
  level: null,
  para: {},
};

/** A single-block byte selection resolved from the Canvas caret/selection. */
interface BlockSel {
  readonly blockOrd: number;
  readonly start: number;
  readonly end: number;
}

/** ONE block that took a comment Mark. */
export interface PlacedCommentSpan {
  readonly blockOrd: number;
  readonly start: number;
  readonly end: number;
  readonly fragment: DoczFragment;
}

/** ONE block inside the range that could NOT take the Mark, and why. */
export interface DeclinedCommentSpan {
  readonly blockOrd: number;
  readonly reason: string;
}

/** The outcome of placing one comment over a (possibly cross-block) range. */
export interface PlacedComment {
  readonly spans: readonly PlacedCommentSpan[];
  readonly declined: readonly DeclinedCommentSpan[];
}

/**
 * Do two fragments carry the SAME content — same block kind/level and the same
 * marked-run projection? Compared in docz's own canonical form
 * (`normalizeFragmentRuns` merges adjacent equal-mark runs, `sameMarks` is the
 * order-free mark-set equality), so a verb that only RE-SEGMENTED the runs (a
 * split with no mark change) reads as "nothing happened" — which it is. RULE
 * ZERO: never a hand-rolled mark diff.
 *
 * This is the honesty predicate for the whole layer: a verb whose result equals
 * its input changed ZERO bytes and ZERO pixels, and must NOT be committed nor
 * reported as applied.
 */
function sameFragment(a: DoczFragment, b: DoczFragment): boolean {
  if ((a.kind ?? "paragraph") !== (b.kind ?? "paragraph")) return false;
  if ((a.level ?? 0) !== (b.level ?? 0)) return false;
  const ra = normalizeFragmentRuns(a.runs);
  const rb = normalizeFragmentRuns(b.runs);
  if (ra.length !== rb.length) return false;
  for (let i = 0; i < ra.length; i++) {
    const x = ra[i];
    const y = rb[i];
    if (!x || !y) return false;
    if (x.text !== y.text || !sameMarks(x.marks, y.marks)) return false;
  }
  return true;
}

export function createCanvasFormatLayer(deps: FormatLayerDeps): CanvasFormatLayer {
  const { getEditor, getHandle, overrides, getRoot } = deps;
  const module = (): DoczModule => getHandle().module;

  /**
   * THE COORDINATE. A `blockOrd` indexes the CANVAS DOCUMENT VIEW — the block
   * list the layout pass encoded — which is `bridge.doczBlocks`, NOT
   * `handle.blocks`.
   *
   * The two are the same array for a document of paragraphs and headings, and a
   * DIFFERENT document the moment the handle carries an opaque `kind:'html'`
   * island: elaborate's server lowers every list, blockquote and code block to
   * one (`src/ingest/html_ingest.zig` `transportKindForTag`), and the canvas
   * explodes it back into real blocks before laying it out. Measured on the live
   * transport shape, a two-item list plus a quote shifts every later ordinal by
   * three — so a verb that resolved `handle.blocks[ord]` formatted, seeded or
   * gated a DIFFERENT block than the caret was in. Falls back to the handle only
   * when no editor is mounted (nothing to be wrong about yet).
   */
  const canvasBlocks = (): readonly DoczBlock[] => {
    const core = getEditor()?.core;
    const view = core ? canvasDoczBlocks(core.getState().bridge) : null;
    return view ?? getHandle().blocks;
  };

  const blockAt = (ord: number): DoczBlock | undefined => canvasBlocks()[ord];

  const blockId = (ord: number): string | undefined => blockAt(ord)?.id;

  /** The focused block ordinal (caret block), or 0 when unmounted. */
  const focusedOrd = (): number => getEditor()?.core.getState().selection.focus.blockOrd ?? 0;

  // ── the STRUCTURED (mark-carrying) view of the document, indexed by block id ──
  //
  // `handle.blocks` is the LEGACY plain-text transport: text + kind + comments,
  // and provably NOT the marks (`DoczBlock` "CANNOT carry inline style marks").
  // The document's real bold/italic/colour/link marks live on
  // `handle.toBlocks()` — the structured block-dto the canvas actually PAINTS
  // through `makeStructuredEncodeBlocks`. Seeding a fragment from the legacy view
  // and committing it as an override therefore OVERLAYS unmarked runs onto a
  // mark-carrying block and DESTROYS the document's own formatting (in the paint
  // and, via `toHtml()`, in the next save). So the seed is taken from the
  // structured view whenever it is resolvable.
  //
  // Indexed BY ID, never by ordinal: an opaque `html` block explodes into several
  // structured blocks, so the two lists are not index-parallel. AMBIGUITY IS
  // RECORDED, NOT SWALLOWED: when one legacy id yields more than one structured
  // block the ordinal cannot say WHICH of them the caret is in, so the id resolves
  // to `null` and every mark read falls back to the plain seed. (The previous
  // first-wins `if (!index.has(sid))` silently attributed the FIRST exploded
  // block's marks to a caret that could be anywhere in the island.)
  //
  // Cached against the handle identity (every edit yields a fresh handle) because
  // `toBlocks()` on a legacy handle re-derives — and re-parses raw HTML — on every
  // call.
  let structuredForHandle: DoczHandle | null = null;
  let structuredBlocks: readonly StructuredBlock[] = [];
  let structuredById: Map<string, StructuredBlock | null> = new Map();
  /** How many TOP-LEVEL structured blocks carry each id (>1 ⇒ ambiguous island). */
  let structuredIdCount: Map<string, number> = new Map();
  const refreshStructured = (): void => {
    const handle = getHandle();
    if (structuredForHandle === handle) return;
    const index = new Map<string, StructuredBlock | null>();
    const counts = new Map<string, number>();
    const blocks = handle.toBlocks();
    for (const sb of blocks) {
      const sid = (sb as { id?: string }).id;
      if (sid == null) continue;
      counts.set(sid, (counts.get(sid) ?? 0) + 1);
      // Only run-bearing kinds that are ALSO a legacy `DoczBlockKind` are
      // addressable as a fragment: a table / image / list / blockquote / code
      // block carries no `runs` the toolbar could mark.
      const runBearing = sb.kind === "paragraph" || sb.kind === "heading";
      index.set(sid, index.has(sid) || !runBearing ? null : sb);
    }
    structuredBlocks = blocks;
    structuredById = index;
    structuredIdCount = counts;
    structuredForHandle = handle;
  };

  /** The runs of a run-bearing structured block. */
  const structuredRuns = (sb: StructuredBlock): readonly FragmentRun[] =>
    (sb as { runs?: readonly FragmentRun[] }).runs ?? [];

  /**
   * The mark-carrying fragment an id names in the STRUCTURED document — the
   * document the canvas paints — or `null` when the id names no single editable
   * run slice.
   *
   * SUB-BLOCK ADDRESSES ARE FIRST-CLASS HERE (the list/quote/table fix). The
   * canvas view flattens a structured `list` to ONE legacy block PER ITEM, each
   * carrying its own address (`b1::li:0`, `b1::li:1` — web-docz
   * `block-address.ts`), and a `blockquote` to one per child (`b1::bq:0`). Those
   * ids are NOT top-level structured ids, so the id index above misses them and
   * the layer used to fall back to a plain-text seed: an item that already
   * carried `<em>` read as unmarked, `activeFormat()` was blind inside it, and a
   * toolbar verb committed an override built from unmarked runs — which would
   * have DESTROYED the item's own italic had the override ever reached the save.
   * `structuredFragmentAt` is web-docz's own address resolver (RULE ZERO — the
   * walk from a container down to one run slice lives upstream in `sub-block.ts`,
   * never re-derived here).
   *
   * AMBIGUITY IS REFUSED, NEVER GUESSED: an opaque `html` island that explodes
   * into two paragraphs stamps BOTH with the legacy block's id, so the id names
   * two different regions and no fragment may be attributed to it.
   */
  const structuredFragmentFor = (id: string | undefined): DoczFragment | null => {
    if (id == null) return null;
    refreshStructured();
    const rootId = parseBlockAddress(id).rootId;
    if ((structuredIdCount.get(id) ?? structuredIdCount.get(rootId) ?? 0) !== 1) return null;
    const top = structuredById.get(id);
    if (top) {
      const level = (top as { level?: number }).level;
      const para = (top as { props?: Record<string, unknown> }).props;
      return {
        kind: top.kind as DoczBlockKind,
        ...(level != null ? { level } : {}),
        runs: structuredRuns(top),
        ...(para != null ? { para } : {}),
      } as DoczFragment;
    }
    // Not a run-bearing top-level block: it may still be a REGION inside one.
    return structuredFragmentAt(structuredBlocks, id);
  };

  /**
   * The block's CANVAS-COORDINATE fragment: the structured, mark-carrying runs
   * the engine actually paints, in the byte space the caret addresses.
   *
   * ⚠ THIS IS THE FIX FOR THE `activeFormat()` BLINDNESS (P3-6). The legacy
   * transport lowers every non-bare block — including any island of inline markup
   * — to `kind:'html'` whose `text` is VERBATIM MARKUP (`html_ingest.TransportKind`).
   * The old seed guarded the structured runs with `runs.join('') === block.text`,
   * i.e. it compared the block's VISIBLE TEXT against its MARKUP; for an `html`
   * block that can never hold, so the layer fell back to `textFragment(block.text)`
   * — ONE unmarked run whose text is `<p><strong>…`. That single line caused all
   * three observed failures: `activeFormat()` reported `{}` over demonstrably bold
   * text, every format verb addressed MARKUP bytes while the caret addressed
   * visible text, and the resulting override was then discarded by
   * `makeStructuredEncodeBlocks`' own text guard so it never painted.
   *
   * `null` when the block has no 1:1 run-bearing structured counterpart (a table,
   * a list, a multi-block island) — those keep the legacy seed unchanged.
   */
  const canvasFragmentFor = (ord: number): DoczFragment | null =>
    structuredFragmentFor(blockAt(ord)?.id);

  /** The text the CANVAS addresses for a block — the structured (visible) text when
   *  the block resolves 1:1, else the legacy `text`. This is the string every
   *  staleness comparison in this layer must use, because it is the string the
   *  caret's byte offsets index and the string `makeStructuredEncodeBlocks`
   *  compares an override against before it agrees to paint it. */
  const canvasText = (ord: number): string => {
    const frag = canvasFragmentFor(ord);
    if (frag) return fragmentText(frag);
    return blockAt(ord)?.text ?? "";
  };

  /** The LEGACY seed: plain text + kind/level + its comment marks. This is the
   *  serialization identity of an opaque `html` block — `renderFragmentToHtml`
   *  re-emits a `kind:'html'` fragment VERBATIM, which is the only reason a table
   *  or a list survives edit→save. Never used as a mark source. */
  const legacySeedOf = (block: DoczBlock | undefined): DoczFragment => {
    if (!block) return textFragment("");
    let frag = textFragment(block.text, { kind: block.kind, level: block.level });
    for (const c of block.comments ?? []) {
      const marked = applyCommentMark(module(), frag, {
        commentId: c.commentId,
        start: c.start,
        end: c.end,
      });
      if (marked.ok) frag = marked.fragment;
    }
    return frag;
  };

  /** The seed the canvas EDITS and READS: the structured, mark-carrying runs in
   *  canvas coordinates when resolvable (a top-level paragraph/heading OR a
   *  sub-block region — a list item, a quote child), else the legacy seed. */
  const seedOf = (block: DoczBlock | undefined): DoczFragment =>
    structuredFragmentFor(block?.id) ?? legacySeedOf(block);

  /** The canvas-ordinal wrapper for {@link seedOf}. */
  const canvasSeed = (ord: number): DoczFragment => seedOf(blockAt(ord));

  /** A session override is honoured ONLY while its text still spells the block's
   *  canvas text — the SAME predicate `makeStructuredEncodeBlocks` applies before
   *  it agrees to overlay one. Without this, `activeFormat()` could report a mark
   *  the encoder has already refused to paint (the inverse lie). */
  const liveOverride = (ord: number): DoczFragment | null => {
    const id = blockId(ord);
    const frag = id != null ? overrides.get(id) : undefined;
    if (!frag) return null;
    return fragmentText(frag) === canvasText(ord) ? frag : null;
  };

  /** The block's fragment for EDIT + MARK-READ: its live override, else the canvas
   *  seed. Always in canvas (visible-text) byte coordinates. */
  const getFragment = (ord: number): DoczFragment =>
    liveOverride(ord) ?? canvasSeed(ord);

  /**
   * The id a format verb may RECORD an override under for canvas ordinal `ord`,
   * or `null` when this block is not writable — in which case the verb declines
   * and the ribbon reports "not applied".
   *
   * A block is writable only when its id names EXACTLY ONE editable run slice in
   * the structured document ({@link structuredFragmentFor}: a top-level
   * paragraph/heading, or a sub-block region such as a list item / quote child).
   * Two shapes fail that and BOTH used to destroy content on save, measured on
   * the real `docz.wasm`:
   *
   *  · a `<table>` / `<pre><code>` island — the canvas view flattens it to one
   *    pseudo-paragraph carrying the ISLAND's id, so the override collided with
   *    the legacy transport block and `toHtml()` replaced the whole table with
   *    `<p><strong>Alpha</strong>\tBeta</p>`. The table was gone.
   *  · an island that explodes into SEVERAL paragraphs — every one is stamped
   *    with the island's id, so an override recorded on the second paragraph
   *    saved as the whole island and DELETED the first
   *    (`<p>First para.</p><p>Second para.</p>` → `<p><strong>Second</strong> para.</p>`).
   *
   * `activeFormat()` already refuses to attribute a mark on those shapes; this is
   * the WRITE half of the same honesty rule, so read and write agree.
   */
  const writableBlockId = (ord: number): string | null => {
    const id = blockId(ord);
    if (id == null) return null;
    // The canvas view must name this block uniquely — two view blocks sharing an
    // id are two regions the override map cannot tell apart.
    let seen = 0;
    for (const b of canvasBlocks()) if (b.id === id) seen += 1;
    if (seen !== 1) return null;
    return structuredFragmentFor(id) ? id : null;
  };

  /**
   * The UNTOUCHED serialization of a LEGACY transport block (`toHtml()` — the Save
   * witness). No session override is consulted here: this is exactly what the block
   * serialized to before any formatting happened, and it is the BYTE-IDENTITY
   * guarantee for every block a verb did not touch.
   *
   *   · the LEGACY seed when the transport kind is the opaque `html` island —
   *     `renderFragmentToHtml` re-emits a `kind:'html'` fragment VERBATIM, which
   *     is the only reason a table / list / code block survives edit→save.
   *   · the CANVAS seed otherwise — the structured, mark-carrying runs, so the
   *     document's own bold/italic/link still reach the save (a plain-text legacy
   *     seed here would silently strip every mark the handle carries).
   *
   * ⚠ SAVE RUNS IN **LEGACY** ORDINALS, NOT CANVAS ORDINALS. Every other function
   * in this layer indexes the canvas view (`blockAt`), because that is the
   * coordinate the caret speaks. `toHtml()` does NOT: it rebuilds the TRANSPORT,
   * block for block, from `handle.blocks`. Feeding it canvas ordinals silently
   * destroyed content — the canvas view flattens a `<table>` island to ONE
   * paragraph of tab-joined cell text ("A\tB", measured), so a table round-tripped
   * through save as `<p>A\tB</p>` and the table was GONE. Caught by
   * `canvasActiveFormat.test.ts` "a table island still round-trips verbatim".
   */
  const untouchedFragment = (block: DoczBlock | undefined): DoczFragment => {
    if (!block) return textFragment("");
    return block.kind === "html" ? legacySeedOf(block) : seedOf(block);
  };

  /**
   * The session overrides whose address ROOTS at this legacy transport block —
   * its own id, or any sub-block address inside it (`b1::li:0`, `b1::bq:0`,
   * `b1::td:0:1`).
   *
   * THIS IS THE JOIN THE SAVE PATH WAS MISSING. A canvas override is keyed by the
   * id of the block the CARET is in, and for a list / blockquote that block is a
   * REGION inside the island (web-docz `block-address.ts`), while `handle.blocks`
   * holds the island as ONE opaque `kind:'html'` block. `overrides.get(block.id)`
   * therefore missed every list-item and quote-child mark — measured live: Bold on
   * a list item moved 478 ink pixels and `toHtml()` came back byte-identical, so
   * the mark was typed, seen, saved and lost.
   */
  const overridesRootedAt = (rootId: string): [string, DoczFragment][] => {
    const out: [string, DoczFragment][] = [];
    for (const [id, frag] of overrides) {
      if (id === rootId || parseBlockAddress(id).rootId === rootId) out.push([id, frag]);
    }
    return out;
  };

  /**
   * Re-serialize ONE legacy transport block with its session overrides written at
   * their REAL addresses — the structured, container-preserving save.
   *
   * The island is re-mounted as a one-block docz document and each override is
   * installed through the upstream, address-aware `DoczHandle.withBlockRuns` verb:
   * it explodes the opaque markup into the structured document ONCE, resolves the
   * address down to the exact run slice (`sub-block.ts` `concretePath` /
   * `writeAtPath`), replaces only that slice, and `toHtml()` re-emits the whole
   * island through docz's mark-general `renderBlockDto`. So `<ul>` stays a `<ul>`,
   * the sibling items keep their own text and marks, and the new mark is IN the
   * `<li>`. RULE ZERO throughout — not one byte of markup is framed here.
   *
   * `null` when the write DECLINED (no address resolved to a run slice): the
   * caller then serializes the block untouched. Losing the mark is honest; the
   * alternative — the old `overrides.get(block.id)` shortcut — replaced a whole
   * `<table>` island with `<p><strong>Alpha</strong>\tBeta</p>` (measured).
   */
  const overriddenIslandHtml = (
    block: DoczBlock,
    targeted: readonly [string, DoczFragment][],
  ): string | null => {
    let h: DoczHandle;
    try {
      h = createDoczHandle(module(), { blocks: [block] });
    } catch {
      return null;
    }
    let wrote = false;
    for (const [id, frag] of targeted) {
      const next = h.withBlockRuns(id, frag);
      if (next === h) continue; // the handle declined this address — honest no-op
      h = next;
      wrote = true;
    }
    return wrote ? h.toHtml() : null;
  };

  /**
   * Install a mutated fragment for a block as ONE UNDOABLE STEP, and re-layout.
   *
   * THE DEFECT THIS REPLACES: this used to be `overrides.set(id, frag)` followed by
   * `editor.refresh()`. Neither half was undoable. `refresh()` deliberately pushes
   * NO snapshot (its own docstring says so), and the override map was an app-level
   * mutable `Map` that `EditorCore`'s `{bridge, selection}` snapshot could not see
   * even if one had been pushed. Measured live: after three applied ribbon commands
   * `canUndo()` was `false` and clicking undo three times did nothing.
   *
   * Both halves are fixed here, and both end at `EditorCore.applyBridge` — the ONE
   * seam that banks a restore point before installing a new document:
   *
   *  · FOLD (preferred): the fragment goes INTO the docz IR via the real
   *    `DoczHandle.withBlockRuns` verb, so the mark stops being a side-car
   *    entirely. Guarded — see {@link foldFragmentIntoHandle} — because that verb
   *    promotes a legacy handle and the promotion can re-shape the block list every
   *    canvas coordinate indexes into.
   *  · CARRY (residue): when the fold is unsafe the fragment rides an IMMUTABLE
   *    override map on the {@link FormatBridge}, which IS the snapshotted state.
   *
   * The live `overrides` map is kept in step because it is the layer's own READ
   * surface (`getFragment`) and the encoder's fallback for a bridge this app did
   * not mint.
   *
   * Returns whether anything was installed — a DECLINE (see
   * {@link writableBlockId}) must reach the ribbon as "not applied", never as a
   * silent success.
   */
  const commit = (ord: number, frag: DoczFragment): boolean => {
    const id = writableBlockId(ord);
    if (id == null) return false;
    const core = getEditor()?.core;
    if (!core) {
      // No mounted core (a verb before mount): there is nothing to relayout and
      // nothing to snapshot. Keep the fragment so the first layout paints it.
      overrides.set(id, frag);
      return true;
    }
    const bridge = core.getState().bridge;
    const handle = getHandle();
    const folded = foldFragmentIntoHandle(handle, id, frag);
    // The next override map: the fold REMOVES the entry (the mark now lives in the
    // IR); the carry REPLACES it. Built from the live map so a bridge minted
    // elsewhere (the paste path) never silently drops the session's other marks.
    const next = new Map(overridesOf(bridge) ?? overrides);
    if (folded) next.delete(id);
    else next.set(id, frag);
    overrides.clear();
    for (const [k, v] of next) overrides.set(k, v);
    core.applyBridge(new FormatBridge(folded ?? handle, next));
    return true;
  };

  /**
   * THE SELECTION, in the coordinate every format act works in: one slice per
   * block the selection touches, in document order.
   *
   * ⛔ This used to COLLAPSE a cross-block selection to a caret on the focus
   * block, and everything downstream inherited that ceiling: `runOp` declines a
   * collapsed range, so selecting a paragraph + a list + a table cell and
   * choosing a font size applied it to NOTHING, silently, while the ribbon read
   * the focus block's caret style and reported a value the other blocks did not
   * have. Both halves of the owner's report were that one line.
   *
   * `core.selectionSlices()` is the ONE derivation of "what does this selection
   * cover" — the same seam a multi-block comment already anchors through, and it
   * descends into list items and table cells because the canvas view flattens a
   * container into one block PER EDITABLE REGION.
   *
   * A COLLAPSED caret produces no slices upstream (there is no range), but it is
   * still a position with a style — the ribbon must show the surrounding text's
   * size there, which is the commonest read of all. So a caret becomes a single
   * zero-width slice, and `resolveStyle` answers it through `caretMarks`' own
   * boundary rules (left of the caret; the first run at offset 0; the last run at
   * the end of a block).
   */
  const slices = (): StyleSlice[] => {
    const core = getEditor()?.core;
    if (!core) return [];
    const { anchor, focus } = core.getState().selection;
    if (anchor.blockOrd === focus.blockOrd && anchor.byteOffset === focus.byteOffset) {
      return [{ blockOrd: focus.blockOrd, start: focus.byteOffset, end: focus.byteOffset }];
    }
    return core
      .selectionSlices()
      .map((s) => ({ blockOrd: s.blockOrd, start: s.start, end: s.end }));
  };

  /** The FOCUSED block's byte range — the coordinate the block-scoped verbs
   *  (`setKind`, `setParaProps`) work in. Inline formatting does NOT use this:
   *  it spans blocks and goes through {@link slices}. */
  const selection = (): BlockSel | null => {
    const core = getEditor()?.core;
    if (!core) return null;
    const { anchor, focus } = core.getState().selection;
    if (anchor.blockOrd !== focus.blockOrd) {
      return { blockOrd: focus.blockOrd, start: focus.byteOffset, end: focus.byteOffset };
    }
    return {
      blockOrd: focus.blockOrd,
      start: Math.min(anchor.byteOffset, focus.byteOffset),
      end: Math.max(anchor.byteOffset, focus.byteOffset),
    };
  };

  // ── the FragmentEditorHandle over the FOCUSED block (reused by FormatApi) ──────
  const fragmentHandle: FragmentEditorHandle = {
    getValue: () => getFragment(selection()?.blockOrd ?? focusedOrd()),
    selectionRange: () => {
      const s = selection();
      return s ? { start: s.start, end: s.end } : null;
    },
    applyTextEdit: (edit: TextEdit) => {
      const ok = applyTextEditToBlock(focusedOrd(), edit);
      return ok ? getFragment(focusedOrd()) : null;
    },
    format: (mark, range) =>
      runOp((frag, r) => applyStyleMark(module(), frag, mark, r), range),
    toggle: (mark, range) =>
      runOp((frag, r) => toggleStyleMark(module(), frag, mark, r), range),
    clearFormatting: (range) =>
      runOp((frag, r) => clearFragmentMarks(module(), frag, r), range),
    changeCase: (mode, range) =>
      runOp((frag, r) => changeFragmentCase(module(), frag, r, mode), range),
    comment: (commentId, range) =>
      runOp(
        (frag, r) => applyCommentMark(module(), frag, { commentId, start: r.start, end: r.end }),
        range,
      ),
    setKind: (kind, level) => {
      const ord = selection()?.blockOrd ?? focusedOrd();
      const frag = setFragmentKind(getFragment(ord), kind, level);
      return commit(ord, frag) ? frag : null;
    },
    // FEATURE BUNDLE 3 (W3 §3A): paragraph geometry now HAS a `pl_layout` reflow
    // carrier — the structured encoder threads `frag.para` (→ block-DTO ParaProps →
    // wasm → SoA V3 drop-cap) and render.ts paints it. Merge the patch onto the
    // block fragment's `.para` transport field (the SAME pure field-merge
    // `setParaPropsOp` performs) and commit → relayout → repaint.
    setParaProps: (patch: ParaPropsPatch) => {
      const ord = selection()?.blockOrd ?? focusedOrd();
      if (ord < 0) return null;
      const frag = getFragment(ord) as DoczFragment & { readonly para?: Record<string, unknown> };
      const nextPara = { ...(frag.para ?? {}), ...(patch as Record<string, unknown>) };
      const next = { ...frag, para: nextPara } as DoczFragment;
      return commit(ord, next) ? next : null;
    },
    toHtml: () => documentHtml(),
    get root() {
      return getRoot();
    },
  };

  /**
   * Run a single-block fragment op over the current selection (or an explicit
   * byte range on the focus block), store + relayout, return the new fragment.
   *
   * DECLINES (returns `null`, committing NOTHING) in the two cases where the verb
   * cannot change anything:
   *
   * 1. A **collapsed range** (`start === end` — a caret with no selection, which is
   *    also what a cross-block selection resolves to). Every docz fragment verb
   *    documents a zero-length range as a no-op that hands the fragment straight
   *    back; taking that as a success used to commit a fresh override + a full
   *    relayout for a click that changed zero bytes and zero pixels, and reported
   *    it as applied.
   * 2. **No delta** — the verb returned content-identical output (e.g. docz
   *    swallowing an unsafe `link` href). Committing it would install a redundant
   *    override; reporting it as applied would be a lie.
   *
   * Because the whole `FormatApi` derives `applied` from whether the handle
   * returned a fragment, declining here is what makes the ribbon toggle honest.
   */
  function runOp(
    op: (
      frag: DoczFragment,
      range: { start: number; end: number },
    ) => { ok: true; fragment: DoczFragment } | { ok: false; errCode: number },
    range?: { start: number; end: number },
  ): DoczFragment | null {
    // An EXPLICIT range is always the focused block (a caller that already knows
    // its target). Otherwise the act covers the whole selection, however many
    // blocks that is.
    const targets: StyleSlice[] = range
      ? [{ blockOrd: focusedOrd(), start: range.start, end: range.end }]
      : slices();
    if (targets.length === 0 || targets[0]!.blockOrd < 0) return null;

    const res = applyAcrossSlices(
      targets,
      (ord) => getFragment(ord),
      // Zero-delta must reach the fold as "no change", so it is reported as not
      // applied rather than committed as a redundant override. The fold compares
      // by identity; `sameFragment` is the content comparison this layer needs
      // (docz hands back a fresh object even when it changed nothing).
      (frag, r) => {
        const out = op(frag, r);
        if (!out.ok) return out;
        return { ok: true, fragment: sameFragment(frag, out.fragment) ? frag : out.fragment };
      },
      // A block that names no single editable run slice (a table or code island)
      // REFUSES here. Reporting that as applied is what let a mark be typed,
      // seen, saved and lost.
      (ord, frag) => commit(ord, frag),
    );

    if (res.applied.length === 0) return null;
    // The ribbon's `applied` signal is per-gesture, and its `fragment` return is
    // the FOCUSED block's — the one the caret is in. A gesture that changed other
    // blocks but not the focused one is still applied, so fall back to the first
    // block that did change rather than reporting a no-op.
    const focus = focusedOrd();
    const ord = res.applied.includes(focus) ? focus : (res.applied[0] as number);
    return getFragment(ord);
  }

  function applyTextEditToBlock(ord: number, edit: TextEdit): boolean {
    const core = getEditor()?.core;
    if (!core || blockAt(ord) == null) return false;
    core.editBlockAt(ord, edit.at, edit.deleteBytes, edit.insert);
    return true;
  }

  /**
   * THE SAVE WITNESS — the exact string the workbench posts.
   *
   * ⛔ P0, MEASURED LIVE (release `20260728-203807-7b27274`, doc 11): this used to
   * walk `handle.blocks` UNCONDITIONALLY. On a **STRUCTURED** handle that list is
   * not the document — it is `legacyBlocksFromStructured()`, which web-docz itself
   * documents as "THIN, LOSSY-BY-DESIGN … DROPS every style mark + table/image
   * structure". Its table arm is `text: tableText(block), kind: 'paragraph'`, so
   * every real `<table>` serialized as `<p>Feld\tWert\nLizenz\t…</p>`; its
   * `pageBreak`/`spacer`/`bookmark` arms return `[]`, so those blocks vanished.
   * One Save on a delivered casino review replaced all 8 tables with flat text.
   *
   * The serializer was correct when it was written, for the handle flavour that
   * existed then. `lib/doczHandle.ts` later began PREFERRING the full-fidelity
   * `body_dto_b64` channel (rightly — the plain-text channel was stripping marks),
   * and every real document became a structured handle. Paint moved to
   * `toBlocks()`; serialize stayed on `blocks`. That asymmetry WAS the shredder.
   *
   * The two flavours have OPPOSITE lossless paths, so the branch is explicit:
   *
   *  · **STRUCTURED** — the structured blocks ARE the document. Fold every
   *    residual session override into the IR through the address-aware
   *    `DoczHandle.withBlockRuns` verb, then let `DoczHandle.toHtml()` render the
   *    whole document through docz's mark-general `renderBlockDto`. Tables, lists,
   *    quotes, images, rules and page breaks all round-trip; RULE ZERO, not one
   *    byte of markup is framed here.
   *  · **LEGACY** — `handle.blocks` IS the truth and an opaque `kind:'html'` block
   *    holds VERBATIM markup that must be re-emitted byte-for-byte. Folding here
   *    would PROMOTE the handle and re-shape every untouched island, so the
   *    per-block path stays exactly as it was (pinned by
   *    `canvasSubBlockSave.test.ts`).
   */
  function documentHtml(): string {
    const handle = getHandle();
    if (handle.structured) return structuredDocumentHtml(handle);
    const mod = module();
    return handle.blocks
      .map((block) => {
        const targeted = block.id != null ? overridesRootedAt(block.id) : [];
        if (targeted.length > 0) {
          const html = overriddenIslandHtml(block, targeted);
          if (html != null) return html;
        }
        return renderFragmentToHtml(mod, untouchedFragment(block));
      })
      .join("\n");
  }

  /**
   * Serialize a STRUCTURED handle losslessly: install each residual override at
   * its REAL address (a top-level block id, or a sub-block address such as
   * `b1::li:0` / `b1::td:0:1` — `withBlockRuns` resolves both), then export the
   * whole document through `toHtml()`.
   *
   * An override the handle DECLINES (no address resolves to a run slice) is left
   * out and the document is written without it. Losing one session mark is
   * honest; the alternative — the old per-block shortcut — destroyed the block.
   */
  function structuredDocumentHtml(handle: DoczHandle): string {
    let h = handle;
    for (const [id, frag] of overrides) {
      const next = h.withBlockRuns(id, frag);
      if (next !== h) h = next;
    }
    return h.toHtml();
  }

  /**
   * The structure `html` would destroy relative to the LIVE document. See
   * {@link CanvasFormatLayer.structuralLossOnSave}.
   */
  function structuralLossOf(html: string): readonly StructuralLossItem[] {
    if (typeof document === "undefined") return [];
    const before = censusOfBlocks(getHandle().toBlocks());
    const after = censusOfHtml(html, document as unknown as Parameters<typeof censusOfHtml>[1]);
    return structuralLoss(before, after);
  }

  const formatApi = createFragmentFormatApi(fragmentHandle, module());

  /**
   * THE READ, over the WHOLE selection — the value the ribbon renders.
   *
   * The single-fragment `formatApi.active()` still owns everything that is a
   * property of the block the caret is IN (its kind, its heading level, its
   * paragraph geometry): those are not range questions and asking them of six
   * blocks at once has no meaning. Everything that IS a range question — the
   * mark set and every valued character property — comes from the ONE resolve
   * over the ONE slice coordinate.
   *
   * The valued properties go through `resolveStyle` rather than being read off
   * the mark set, because a mark intersection cannot tell "every run renders at
   * 10pt" (agreement) from "no run carries a size mark" (also agreement, and
   * previously reported as blank). `MIXED` is the only thing that blanks a
   * control, and it means exactly one thing: the selection genuinely disagrees.
   */
  function activeFormatAcrossSelection(): ActiveFormat {
    const base = formatApi.active();
    const sel = slices();
    if (sel.length === 0) return base;

    const at = (ord: number) => getFragment(ord);
    const marks = marksAcrossSlices(sel, at);
    const link = marks.find((m) => m.type === "link");

    /** `MIXED` is the ribbon's BLANK. A control renders no value, never a guess. */
    const val = <T,>(prop: Parameters<typeof resolveStyle<T>>[2]): T | null => {
      const got = resolveStyle(sel, at, prop);
      return got === MIXED ? null : got;
    };

    const underline = val(STYLE_PROPS.underlineStyle);
    const highlight = val(STYLE_PROPS.highlight);

    return {
      ...base,
      marks: new Set(marks.map((m) => m.type)),
      link: link && link.type === "link" ? link.href : null,
      color: val(STYLE_PROPS.color),
      // `transparent` is the INHERITED no-highlight value, not a colour to show.
      highlight: highlight === "transparent" ? null : highlight,
      fontFamily: val(STYLE_PROPS.fontFamily),
      fontSize: val(STYLE_PROPS.fontSize),
      // `none` is the inherited not-underlined value; the style menu shows blank.
      underlineStyle: underline === "none" ? null : underline,
      letterSpacing: val(STYLE_PROPS.letterSpacing),
    };
  }

  return {
    formatApi,
    /**
     * A toggle is ONE decision followed by a UNIFORM apply, never a per-block
     * re-decision.
     *
     * Asking docz's native toggle verb per slice would let a selection covering a
     * bold paragraph and a plain list item UN-bold the first while bolding the
     * second — each block answering "am I fully covered?" for itself. What the
     * user asked for is one gesture over one selection: if the WHOLE selection is
     * uniformly marked it comes off everywhere, otherwise it goes on everywhere
     * (`applyStyleMark` is additive and idempotent, so already-marked blocks in a
     * partial selection are unharmed).
     *
     * That is the same "as if every one had been selected separately and given the
     * same value" rule the valued properties follow — the value here is simply
     * on-or-off, decided once from {@link marksAcrossSlices}.
     */
    toggleMark: (type: ToggleMarkType): ToggleResult => {
      const sel = slices();
      const wasActive = marksAcrossSlices(sel, (ord) => getFragment(ord)).some(
        (m) => m.type === type,
      );
      if (wasActive && !module().supportsMarkToggle) {
        // Older docz.wasm (pre-bit5) cannot remove a mark — honest pending, never
        // a fabricated removal.
        return { fragment: null, applied: false, toggledOff: false, pending: "toggle-off" };
      }
      const mark = { type } as StyleMark;
      const fragment = runOp((frag, r) =>
        wasActive
          ? toggleStyleMark(module(), frag, mark, r)
          : applyStyleMark(module(), frag, mark, r),
      );
      if (fragment == null) return { fragment: null, applied: false, toggledOff: false };
      return { fragment, applied: !wasActive, toggledOff: wasActive };
    },
    setMark: (mark) => fragmentHandle.format(mark),
    clearFormatting: () => fragmentHandle.clearFormatting(),
    changeCase: (mode) => fragmentHandle.changeCase(mode),
    setBlockKind: (kind, level) => fragmentHandle.setKind(kind, level),
    setParaProps: (patch) => fragmentHandle.setParaProps(patch),
    placeComment: (commentId, range) => {
      // The target ranges: an explicit range on the focused block, or the live
      // canvas selection decomposed into its PER-BLOCK sub-ranges. `rangeSlices`
      // (upstream, `EditorCore.selectionSlices`) is the ONE derivation of "what
      // does this selection cover" — a joined string cannot say where the block
      // boundaries fall, which is precisely why applying a Mark across N blocks
      // needs it and cannot use `selectedText()`.
      const targets: StyleSlice[] = range
        ? [{ blockOrd: focusedOrd(), start: range.start, end: range.end }]
        : slices();
      if (targets.length === 0) return null;
      // A single-block ZERO-WIDTH range is a bare caret: no range at all, and the
      // one case that must still refuse outright.
      if (targets.length === 1 && targets[0]!.start === targets[0]!.end) return null;

      // ⇒ THE SAME APPLY the ribbon's formatting uses. A comment Mark is not a
      // special case of "put a mark on every block of a selection" — it IS that
      // operation, and it was the only caller that ever did it correctly. Running
      // both through one fold is what keeps them from drifting apart again.
      const res = applyAcrossSlices(
        targets,
        (ord) => getFragment(ord),
        (frag, r) => applyCommentMark(module(), frag, { commentId, start: r.start, end: r.end }),
        (ord, frag) => commit(ord, frag),
      );

      const applied = new Set(res.applied);
      const spans: PlacedCommentSpan[] = targets
        .filter((t) => applied.has(t.blockOrd))
        .map((t) => ({
          blockOrd: t.blockOrd,
          start: t.start,
          end: t.end,
          fragment: getFragment(t.blockOrd),
        }));
      const declined: DeclinedCommentSpan[] = res.declined.map((d) => ({
        blockOrd: d.blockOrd,
        reason: d.reason,
      }));
      return { spans, declined };
    },
    activeFormat: () => {
      // No editor mounted yet → an empty snapshot (never a fabricated active mark).
      if (!getEditor()) return EMPTY_ACTIVE_FORMAT;
      return activeFormatAcrossSelection();
    },
    fragmentFor: (ord) => getFragment(ord),
    applyTextEdit: (id, edit) => {
      const ord = getHandle().blocks.findIndex((b) => b.id === id);
      if (ord < 0) return false;
      return applyTextEditToBlock(ord, edit);
    },
    toHtml: () => documentHtml(),
    structuralLossOnSave: () => structuralLossOf(documentHtml()),
    reconcile: () => {
      const blocks = canvasBlocks();
      for (const [id, frag] of overrides) {
        const ord = blocks.findIndex((b) => b.id === id);
        // Compare against the block's CANVAS text, not its legacy `text`: for an
        // opaque `html` island the legacy text is verbatim markup, so comparing
        // against it deleted every override on such a block on the very next
        // relayout — the mark would apply, repaint, and vanish.
        if (ord < 0 || fragmentText(frag) !== canvasText(ord)) overrides.delete(id);
      }
    },
  };
}
